import { Link, useSearchParams } from 'react-router-dom';
import { persistStateToUrl } from 'utils/urlState';
import { useTranslation } from 'react-i18next';
import { useEffect, useMemo, useRef, useState } from 'react';
import ExpandablePanel from '../../components/ExpandablePanel/ExpandablePanel';
import FilterChips from '../../components/FilterChips/FilterChips';
import { getAllCourts } from '../../services/courtServices';
import { formatDateByLocale, removeTime } from '../../utils/formatDate';
import {
  downloadFileS3,
  getDailyState,
  getDailyStateLaboral,
  getTribunalesUsuarioED,
  updateStatus,
  getFilePreviewUrlS3,
  exportDailyStatesExcel,
} from '../../services/dailyServices';
import CardStats from '../../components/CardStats/CardStats';
import PdfViewer from '../../components/PdfViewer/PdfViewer';
import './dailyState.css';
import { getAllInternalStates } from '../../services/internalStatusServices';
import { getCurrentUser } from '../../services/authService';
import { sortByLocale, sortByNumber } from '../../utils/sortBy';
import { getCreateInfo } from 'services/caseServices';
import { parseArrayParam, parseNumberArrayParam } from 'utils/parseArray';
import { useLanguage } from '../../context/LanguageContext';
import { normalizeSupportedLanguage } from '../../utils/createScopedI18n';
import usePermissionsDailyState from 'permissions/modules/dailyState/usePermissionsDailyState';
import { dailyStateI18n } from './i18n';
import { useSnackbar } from 'context/SnackbarContext';

const DailyState = ({ DesignSystemComponents }) => {
  const { language } = useLanguage();
  const dailyStateLanguage = normalizeSupportedLanguage(language);
  const { t } = useTranslation(undefined, { i18n: dailyStateI18n });
  const { showSnackbar } = useSnackbar();

  const {
    Card,
    IconButton,
    Input,
    Dropdown,
    Table,
    Button,
    ComboBox,
    Modal,
    SvgIcon,
  } = DesignSystemComponents;

  const fetchTimeoutRef = useRef(null);
  const abortControllerRef = useRef(null);

  const {
    canGoToCause: canGoToCaseDetail,
    canManage,
    canDownloadExcel,
  } = usePermissionsDailyState();

  const [searchParams, setSearchParams] = useSearchParams();
  const [previewDoc, setPreviewDoc] = useState(null);
  const [previewUrl, setPreviewUrl] = useState(null);
  const [loadingPreview, setLoadingPreview] = useState(false);
  const [isExportingExcel, setIsExportingExcel] = useState(false);
  const [showInactiveWarning, setShowInactiveWarning] = useState(false);
  const [userId, setUserId] = useState(null);
  const [dailyState, setDailyState] = useState([]);
  const [criticalPendingCount, setCriticalPendingCount] = useState(0);
  const [pendingCount, setPendingCount] = useState(0);
  const [managedCount, setManagedCount] = useState(0);
  const [totalCount, setTotalCount] = useState(0);
  const [dailyStateTablePage, setDailyStateTablePage] = useState(0);
  const [pageSize, setPageSize] = useState(10);
  const defaultCourt = useMemo(
    () => ({ label: t('dailyStates_selectCourt'), value: 0 }),
    [t]
  );
  const [allCourts, setAllCourts] = useState([]);
  const [selectedCourts, setSelectedCourts] = useState(defaultCourt);
  const [selectedCourtsMulti, setSelectedCourtsMulti] = useState([]);
  const [myCourtIds, setMyCourtIds] = useState([]);
  const [loadingMyCourts, setLoadingMyCourts] = useState(false);
  const [myCourtsInitialized, setMyCourtsInitialized] = useState(false);
  const [defaultBusinessAreaId, setDefaultBusinessAreaId] = useState();
  const [businessAreaInitialized, setBusinessAreaInitialized] = useState(false);
  const [hasSearched, setHasSearched] = useState(false);

  const getInternalStateConfig = (state) => {
    if (state === 'Pendiente') {
      return {
        icon: 'hourglass',
        variant: 'warning',
        fill: 'var(--warning-900)',
      };
    }
    if (state === 'No amerita') {
      return { icon: 'circleX', variant: 'info' };
    }
    return {
      icon: 'circleCheck',
      variant: 'success',
      fill: 'var(white)',
    };
  };

  const [internalStatusCatalog, setInternalStatusCatalog] = useState([]);

  const withDocuments = useMemo(
    () => [
      { label: t('dailyStates_all'), value: 'todos' },
      { label: t('dailyStates_yes'), value: true },
      { label: t('dailyStates_no'), value: false },
    ],
    [t]
  );

  const [selectedWithDocuments, setSelectedWithDocuments] = useState(
    withDocuments[0]
  );

  const activeCases = useMemo(
    () => [
      { label: t('dailyStates_allFemale'), value: null },
      { label: t('dailyStates_yes'), value: true },
      { label: t('dailyStates_no'), value: false },
    ],
    [t]
  );

  const [selectedActiveCases, setSelectedActiveCases] = useState(
    activeCases[0]
  );

  const states = useMemo(
    () => [
      { label: t('dailyStates_all'), value: null },
      ...internalStatusCatalog.map((status) => ({
        label: status.name,
        value: status.id,
        color: status.badgeColor,
      })),
    ],
    [internalStatusCatalog, t]
  );
  const [showPanel, setShowPanel] = useState(false);
  const [loading, setLoading] = useState(false);
  const [searchDailyState, setSearchDailyState] = useState('');
  const [businessAreaOptions, setBusinessAreaOptions] = useState([]);
  const [selectedBusinessAreaMulti, setSelectedBusinessAreaMulti] = useState(
    []
  );

  const myCasesOptions = useMemo(
    () => [
      {
        label: t('dailyStates_myCases'),
        value: 'mis causas',
      },
      {
        label: t('dailyStates_allFemale'),
        value: 'todos',
      },
    ],
    [t]
  );

  const [myCases, setMyCases] = useState(myCasesOptions[1]);

  const processOptions = useMemo(
    () => [
      { label: t('dailyStates_process_resolution'), value: 'Resolución' },
      { label: t('dailyStates_process_appeal'), value: 'Apelación' },
      { label: t('dailyStates_process_writ'), value: 'Escrito' },
      {
        label: t('dailyStates_process_receiverAction'),
        value: 'Actuación receptor',
      },
      { label: t('dailyStates_process_notification'), value: 'Notificación' },
    ],
    [t]
  );

  const [selectedProcess, setSelectedProcess] = useState([]);
  const [filters, setFilters] = useState(null);
  const selectedInternalStatus = useMemo(() => {
    if (!states.length) return { label: '', value: null };

    const selectedValue =
      Array.isArray(filters?.States) && filters.States.length > 0
        ? filters.States[0]
        : null;

    return (
      states.find((stateOption) => stateOption.value === selectedValue) ||
      states[0]
    );
  }, [filters?.States, states]);

  useEffect(() => {
    setMyCases(
      (prev) =>
        myCasesOptions.find((option) => option.value === prev?.value) ||
        myCasesOptions[1]
    );
  }, [myCasesOptions]);

  useEffect(() => {
    setSelectedWithDocuments(
      (prev) =>
        withDocuments.find((option) => option.value === prev?.value) ||
        withDocuments[0]
    );
  }, [withDocuments]);

  useEffect(() => {
    setSelectedActiveCases(
      (prev) =>
        activeCases.find((option) => option.value === prev?.value) ||
        activeCases[0]
    );
  }, [activeCases]);

  const resolveIsLaboralRole = () => {
    const authUser = getCurrentUser();
    const roleId = Number(
      authUser?.roleId ?? authUser?.role?.id ?? authUser?.role
    );
    console.log(roleId);
    if (Number.isFinite(roleId)) return roleId === 23;
    if (typeof authUser?.role === 'string') {
      return authUser.role.toLowerCase() === 'laboral';
    }
    return false;
  };

  const myCourtsOption = useMemo(() => {
    if (!myCourtIds.length) return null;
    return { label: t('dailyStates_myCourts'), value: 'mine' };
  }, [myCourtIds.length, t]);

  const courtsOptions = useMemo(() => {
    return [
      defaultCourt,
      ...(myCourtsOption ? [myCourtsOption] : []),
      ...allCourts,
    ];
  }, [allCourts, defaultCourt, myCourtsOption]);

  const buildFilterChips = () => {
    if (!filters) return [];

    const chips = [];

    if (filters.SearchDailyState) {
      chips.push({
        id: 'search',
        label: t('dailyStates_chip_search'),
        value: filters.SearchDailyState,
      });
    }

    if (filters.MyCases && filters.MyCases !== 'mis causas') {
      chips.push({
        id: 'myCases',
        label: t('dailyStates_chip_cases'),
        value: myCases?.label || filters.MyCases,
      });
    }

    // Courts: prefer multi-select, fallback to single select
    if (selectedCourtsMulti && selectedCourtsMulti.length > 0) {
      chips.push({
        id: 'courts',
        label: t('dailyStates_chip_courts'),
        value: selectedCourtsMulti.map((c) => c.label).join(', '),
      });
    } else if (
      selectedCourts &&
      selectedCourts.value &&
      selectedCourts.value !== 0
    ) {
      chips.push({
        id: 'courts',
        label: t('dailyStates_chip_court'),
        value: selectedCourts.label,
      });
    }

    // Dates
    if (filters.StartDate || filters.EndDate) {
      const start = filters.StartDate;
      const end = filters.EndDate;

      let value = '';
      if (start && end && start !== end) {
        value = `${removeTime(start)} - ${removeTime(end)}`;
      } else {
        value = removeTime(start || end);
      }

      chips.push({
        id: 'date',
        label: t('dailyStates_chip_date'),
        value,
      });
    }

    if (filters.Role) {
      chips.push({
        id: 'role',
        label: t('dailyStates_chip_role'),
        value: filters.Role,
      });
    }

    if (filters.Case) {
      chips.push({
        id: 'case',
        label: t('dailyStates_chip_case'),
        value: filters.Case,
      });
    }

    if (filters.Process && filters.Process.length > 0) {
      const value =
        selectedProcess && selectedProcess.length > 0
          ? selectedProcess.map((p) => p.label).join(', ')
          : filters.Process.join(', ');

      chips.push({
        id: 'process',
        label: t('dailyStates_chip_process'),
        value,
      });
    }

    if (filters.States && filters.States.length > 0) {
      const activeStates = states.filter((s) =>
        filters.States.includes(s.value)
      );
      const value = activeStates.length
        ? activeStates.map((s) => s.label).join(', ')
        : filters.States.join(', ');

      chips.push({
        id: 'states',
        label: t('dailyStates_chip_internalStatus'),
        value,
      });
    }

    if (filters.HasDocument !== null && filters.HasDocument !== undefined) {
      let value = t('dailyStates_all');
      if (filters.HasDocument === true) value = t('dailyStates_yes');
      if (filters.HasDocument === false) value = t('dailyStates_no');

      chips.push({
        id: 'hasDocument',
        label: t('dailyStates_chip_withDocument'),
        value,
      });
    }

    return chips;
  };

  const handleRemoveFilterChip = (id) => {
    if (!filters) return;

    switch (id) {
      case 'search': {
        setSearchDailyState('');
        handleFilterChange('SearchDailyState', '');
        break;
      }
      case 'myCases': {
        const defaultMyCases = myCasesOptions[1];
        setMyCases(defaultMyCases);
        handleFilterChange('MyCases', defaultMyCases.value);
        break;
      }
      case 'courts': {
        const fallback = myCourtsOption ?? defaultCourt;
        setSelectedCourts(fallback);
        setSelectedCourtsMulti([]);
        handleFilterChange(
          'Courts',
          fallback.value === 'mine' ? myCourtIds : []
        );
        break;
      }
      case 'date': {
        handleFilterChange('StartDate', null);
        handleFilterChange('EndDate', null);
        break;
      }
      case 'role': {
        handleFilterChange('Role', '');
        break;
      }
      case 'case': {
        handleFilterChange('Case', '');
        break;
      }
      case 'process': {
        setSelectedProcess([]);
        handleFilterChange('Process', []);
        break;
      }
      case 'states': {
        handleFilterChange('States', [1]);
        break;
      }
      case 'hasDocument': {
        setSelectedWithDocuments(withDocuments[0]);
        handleFilterChange('HasDocument', null);
        break;
      }
      default:
        break;
    }
  };

  const handleClearAllFilterChips = () => {
    // Clear advanced filters
    handleClearFilters();

    // Clear header-level filters
    setSearchDailyState('');
    handleFilterChange('SearchDailyState', '');

    const defaultMyCases = myCasesOptions[1];
    setMyCases(defaultMyCases);
    handleFilterChange('MyCases', defaultMyCases.value);

    const fallback = myCourtsOption ?? defaultCourt;
    setSelectedCourts(fallback);
    setSelectedCourtsMulti([]);
    handleFilterChange('Courts', fallback.value === 'mine' ? myCourtIds : []);

    handleFilterChange('States', [1]);
  };

  const sortFns = {
    code: sortByLocale('code'),
    title: sortByLocale('title'),
    courtName: sortByLocale('courtName'),
    businessArea: sortByLocale('businessArea'),
    caseStateProcessId: sortByNumber('caseStateProcessId'),
    process: sortByLocale('process'),
    description: sortByLocale('description'),
    date: sortByLocale('date'),
    state: sortByLocale('state'),
  };

  useEffect(() => {
    if (!filters) return;
    setSearchDailyState(filters.SearchDailyState ?? '');
  }, [filters?.SearchDailyState]);

  useEffect(() => {
    if (!filters) return;

    const status = String(filters.InternalStateStatus ?? '').toLowerCase();
    const nextSelected =
      status === 'activa'
        ? activeCases[1]
        : status === 'inactiva'
          ? activeCases[2]
          : activeCases[0];

    setSelectedActiveCases(nextSelected);
  }, [filters?.InternalStateStatus, activeCases]);

  useEffect(() => {
    if (!filters || !businessAreaOptions.length) return;

    const selected = businessAreaOptions.filter((opt) =>
      filters.BusinessAreaIds?.includes(opt.value)
    );

    setSelectedBusinessAreaMulti(selected);
  }, [filters?.BusinessAreaIds, businessAreaOptions]);

  useEffect(() => {
    if (!filters) return;

    const selected = withDocuments.find((opt) => {
      if (filters.HasDocument === null) return opt.value === 'todos';
      return opt.value === filters.HasDocument;
    });

    setSelectedWithDocuments(selected ?? withDocuments[0]);
  }, [filters?.HasDocument]);

  useEffect(() => {
    if (!filters) return;

    const courtIds = filters.Courts ?? [];

    if (
      myCourtsOption &&
      myCourtIds.length > 0 &&
      courtIds.length === myCourtIds.length &&
      myCourtIds.every((id) => courtIds.includes(id))
    ) {
      setSelectedCourts(myCourtsOption);
      return;
    }

    if (courtIds.length === 1) {
      const found = courtsOptions.find((c) => c.value === courtIds[0]);
      setSelectedCourts(found ?? defaultCourt);
      return;
    }

    setSelectedCourts(defaultCourt);
  }, [
    filters?.Courts,
    courtsOptions,
    defaultCourt,
    myCourtsOption,
    myCourtIds,
  ]);

  useEffect(() => {
    if (!filters || !Array.isArray(filters.Process)) return;

    const selected = processOptions.filter((opt) =>
      filters.Process.includes(opt.value)
    );

    setSelectedProcess(selected);
  }, [filters?.Process, processOptions]);

  useEffect(() => {
    dailyStateI18n.changeLanguage(dailyStateLanguage);
  }, [dailyStateLanguage]);

  useEffect(() => {
    if (!userId) return;

    const bootstrap = async () => {
      try {
        await Promise.all([
          fetchInternalStatus(),
          fetchCourts(),
          fetchBusinessArea(),
        ]);
      } catch (e) {
        console.error('Error en bootstrap DailyState', e);
      }
    };

    bootstrap();
  }, [userId]);

  useEffect(() => {
    const authUser = getCurrentUser();
    if (authUser?.userId) {
      setUserId(authUser.userId);
    }
  }, []);

  useEffect(() => {
    if (!filters || !filters.UserId) return;
    if ([...searchParams.keys()].length === 0) return;

    if (fetchTimeoutRef.current) {
      clearTimeout(fetchTimeoutRef.current);
    }

    fetchTimeoutRef.current = setTimeout(() => {
      fetchDailyState(filters);
    }, 300);
  }, [filters]);

  useEffect(() => {
    if (!userId) return;

    const isActiveParam = searchParams.get('IsActive');
    const restoredIsActive =
      isActiveParam === null ? null : isActiveParam === 'true';
    const internalStateParam = searchParams.get('InternalStateStatus');
    const restoredInternalStateStatus =
      internalStateParam ??
      (restoredIsActive === null
        ? null
        : restoredIsActive
          ? 'Activa'
          : 'Inactiva');

    const restoredFilters = {
      UserId: userId,
      SearchDailyState: searchParams.get('SearchDailyState') ?? '',
      MyCases: searchParams.get('MyCases') ?? 'todos',
      HasDocument:
        searchParams.get('HasDocument') === null
          ? null
          : searchParams.get('HasDocument') === 'true',
      IsActive: restoredIsActive,
      InternalStateStatus: restoredInternalStateStatus,
      Courts: parseNumberArrayParam(searchParams.get('Courts')),
      StartDate: searchParams.get('StartDate'),
      EndDate: searchParams.get('EndDate'),
      Role: searchParams.get('Role') ?? '',
      Case: searchParams.get('Case') ?? '',
      Process: parseArrayParam(searchParams.get('Process')),
      States: parseNumberArrayParam(searchParams.get('States')),
      BusinessAreaIds: parseNumberArrayParam(
        searchParams.get('BusinessAreaIds')
      ),
    };

    setFilters(restoredFilters);

    const restoredPage = Number(searchParams.get('page') ?? 0);
    const restoredPageSize = Number(searchParams.get('pageSize') ?? 10);

    setDailyStateTablePage(restoredPage);
    setPageSize(restoredPageSize);
  }, [userId]);

  useEffect(() => {
    if (!userId) return;

    if ([...searchParams.keys()].length > 0) return;

    setMyCourtsInitialized(false);
    setBusinessAreaInitialized(false);
    setDefaultBusinessAreaId(undefined);
    setFilters({
      UserId: userId,
      SearchDailyState: '',
      MyCases: 'todos',
      HasDocument: null,
      IsActive: null,
      InternalStateStatus: null,
      Courts: [],
      StartDate: null,
      EndDate: null,
      Role: '',
      Case: '',
      Process: [],
      States: [],
      BusinessAreaIds: [],
    });
  }, [userId]);

  useEffect(() => {
    if (!userId) return;
    let mounted = true;

    const fetchMyCourts = async () => {
      try {
        setLoadingMyCourts(true);
        const resp = await getTribunalesUsuarioED(userId);
        if (!mounted) return;
        const tribunales = Array.isArray(resp) ? resp : resp?.tribunalIds;
        const ids = Array.isArray(tribunales)
          ? tribunales
              .map((item) =>
                Number(
                  typeof item === 'number' ? item : (item?.id ?? item?.value)
                )
              )
              .filter((id) => Number.isFinite(id))
          : [];
        const areaId = resp?.businessAreaId ?? resp?.businessareaid ?? null;
        if (areaId !== null && areaId !== undefined && areaId !== '') {
          const parsedAreaId = Number(areaId);
          setDefaultBusinessAreaId(
            Number.isFinite(parsedAreaId) ? parsedAreaId : null
          );
        } else {
          setDefaultBusinessAreaId(null);
        }
        setMyCourtIds(ids);
      } catch (error) {
        console.error('Error al cargar mis tribunales:', error);
      } finally {
        if (mounted) {
          setLoadingMyCourts(false);
        }
      }
    };

    fetchMyCourts();

    return () => {
      mounted = false;
    };
  }, [userId]);

  const fetchDailyState = async (customFilters = filters) => {
    if (!customFilters || !customFilters.UserId) return;

    if (abortControllerRef.current) {
      abortControllerRef.current.abort();
    }

    const controller = new AbortController();
    abortControllerRef.current = controller;

    setLoading(true);

    try {
      const isLaboralRole = resolveIsLaboralRole();
      const fetchFn = isLaboralRole ? getDailyStateLaboral : getDailyState;
      const resp = await fetchFn(customFilters, {
        signal: controller.signal,
      });

      setDailyState(resp);
      setTotalCount(resp[0]?.todayCount ?? 0);
      setCriticalPendingCount(resp[0]?.criticalPendingCount ?? 0);
      setPendingCount(resp[0]?.pendingCount ?? 0);
      setManagedCount(resp[0]?.managedCount ?? 0);
      setHasSearched(true);
    } catch (err) {
      if (err.name === 'CanceledError' || err.code === 'ERR_CANCELED') {
        return;
      }

      setDailyState([]);

      setTotalCount(0);
      setCriticalPendingCount(0);
      setPendingCount(0);
      setManagedCount(0);
    } finally {
      if (!controller.signal.aborted) {
        setLoading(false);
      }
    }
  };

  const handlePageSizeChange = (newSize) => {
    setPageSize(newSize);
    setDailyStateTablePage(0);

    persistStateToUrl({
      filters,
      page: 0,
      pageSize: newSize,
      setSearchParams,
    });
  };

  const fetchCourts = async () => {
    const resp = await getAllCourts();
    setAllCourts(Array.isArray(resp) ? resp : []);
  };

  useEffect(() => {
    if (!filters || myCourtsInitialized) return;
    if (!myCourtsOption || myCourtIds.length === 0) return;
    if (defaultBusinessAreaId === undefined) return;

    if (Array.isArray(filters.Courts) && filters.Courts.length > 0) {
      setMyCourtsInitialized(true);
      return;
    }

    const nextBusinessAreaIds =
      defaultBusinessAreaId !== null && defaultBusinessAreaId !== undefined
        ? [defaultBusinessAreaId]
        : filters.BusinessAreaIds;

    const nextFilters = {
      ...filters,
      Courts: myCourtIds,
      BusinessAreaIds: nextBusinessAreaIds,
    };

    setSelectedCourts(myCourtsOption);
    setFilters(nextFilters);
    fetchDailyState(nextFilters);
    setMyCourtsInitialized(true);
  }, [
    filters,
    myCourtsInitialized,
    myCourtsOption,
    myCourtIds,
    defaultBusinessAreaId,
  ]);

  useEffect(() => {
    if (businessAreaInitialized) return;
    if (defaultBusinessAreaId === null || defaultBusinessAreaId === undefined)
      return;
    if (!businessAreaOptions.length) return;

    const selected = businessAreaOptions.find(
      (option) => Number(option.value) === Number(defaultBusinessAreaId)
    );

    if (!selected) return;

    setSelectedBusinessAreaMulti([selected]);
    setFilters((prev) =>
      prev ? { ...prev, BusinessAreaIds: [selected.value] } : prev
    );
    setBusinessAreaInitialized(true);
  }, [businessAreaInitialized, businessAreaOptions, defaultBusinessAreaId]);

  const fetchInternalStatus = async () => {
    try {
      const resp = await getAllInternalStates();
      setInternalStatusCatalog(Array.isArray(resp) ? resp : []);
    } catch (error) {
      console.error(error);
    }
  };

  const fetchBusinessArea = async () => {
    try {
      const resp = await getCreateInfo();

      const mapped = resp.businessArea
        .filter((u) => u.id !== 2 && u.id !== 11)
        .map((u) => ({
          label: u.name,
          value: u.id,
          ...u,
        }));

      setBusinessAreaOptions(mapped);
    } catch (error) {}
  };

  const handleFilterChange = (field, value) => {
    setFilters((prev) => {
      const next = { ...prev, [field]: value };

      persistStateToUrl({
        filters: next,
        page: dailyStateTablePage,
        pageSize,
        setSearchParams,
      });

      return next;
    });
  };

  const handleSearchDailyState = (e) => {
    setDailyStateTablePage(0);
    setSearchDailyState(e.target.value);
    handleFilterChange('SearchDailyState', e.target.value);
  };

  const handleMyCases = (selected) => {
    setMyCases(selected);
    handleFilterChange('MyCases', selected.value);
  };

  const resolveCourtIds = (option) => {
    if (!option) return [];
    if (option.value === 'mine') return myCourtIds;
    if (option.value && option.value !== 0) return [option.value];
    return [];
  };

  const resolveMultiCourtIds = (options) => {
    if (!Array.isArray(options)) return [];
    return options.flatMap((option) => resolveCourtIds(option));
  };

  const syncCourtsFilter = (singleCourt, multiCourts) => {
    const ids = [
      ...resolveCourtIds(singleCourt),
      ...resolveMultiCourtIds(multiCourts),
    ];

    const uniqueIds = [...new Set(ids)];

    handleFilterChange('Courts', uniqueIds);
  };

  const handleCourtsFilter = (selected) => {
    const value = selected ?? courtsOptions[0];
    setSelectedCourts(value);

    syncCourtsFilter(value, selectedCourtsMulti);
  };

  const handleGroupsMultiSelect = (selectedItems) => {
    if (!Array.isArray(selectedItems)) return;

    setSelectedBusinessAreaMulti(selectedItems);

    const selectedValues = selectedItems.map((item) => item.value);

    handleFilterChange('BusinessAreaIds', selectedValues);
  };

  const handleApplyFilters = () => {
    setDailyState([]);
    setDailyStateTablePage(0);

    setFilters((prev) => {
      const nextFilters = { ...prev };

      fetchDailyState(nextFilters);

      return nextFilters;
    });
  };

  const handleClearFilters = () => {
    handleFilterChange('StartDate', null);
    handleFilterChange('EndDate', null);
    handleFilterChange('Role', '');
    handleFilterChange('Case', '');
    handleFilterChange('Process', []);
    handleFilterChange('Courts', []);
    handleFilterChange('States', []);
    handleFilterChange('BusinessAreaIds', []);
    handleFilterChange('HasDocument', null);
    handleFilterChange('IsActive', null);
    handleFilterChange('InternalStateStatus', null);
    setSelectedActiveCases(activeCases[0]);
    setShowInactiveWarning(false);
    setSelectedWithDocuments(withDocuments[0]);
    setSelectedCourtsMulti([]);
    setSelectedCourts([]);
    setSelectedProcess([]);
    setSelectedBusinessAreaMulti([]);
  };

  const handleHasDocument = (selected) => {
    setSelectedWithDocuments(selected);

    let valueToSend = null;

    if (selected.value === true) valueToSend = true;
    else if (selected.value === false) valueToSend = false;
    else if (selected.value === 'todos') valueToSend = null;

    handleFilterChange('HasDocument', valueToSend);
  };

  const handleActiveCases = (selected) => {
    setSelectedActiveCases(selected);

    if (selected.value === null) {
      setShowInactiveWarning(true);
      handleFilterChange('IsActive', null);
      handleFilterChange('InternalStateStatus', null);
      return;
    }

    if (selected.value === true) {
      setShowInactiveWarning(false);
      handleFilterChange('IsActive', true);
      handleFilterChange('InternalStateStatus', 'Activa');
    } else {
      setShowInactiveWarning(true);
      handleFilterChange('IsActive', false);
      handleFilterChange('InternalStateStatus', 'Inactiva');
    }
  };

  const handleState = async (selected, item) => {
    try {
      const payload = {
        dailyStateId: item.dailyStateId,
        date: item.date,
        caseId: item.id,
        code: item.code,
        newCivilCourtCode: item.newCivilCourtCode,
      };

      await updateStatus(payload, selected.value, userId);

      const isPending = selected.label === 'Pendiente';

      let updatedList = isPending
        ? dailyState.map((row) =>
            row.code === item.code
              ? { ...row, state: selected.label, stateId: selected.value }
              : row
          )
        : dailyState.filter((row) => row.code !== item.code);

      setDailyState(updatedList);

      sessionStorage.removeItem('dailyStateCache');

      window.dispatchEvent(new Event('dailyStateUpdated'));
    } catch (error) {
      console.error(error);
    }
  };

  const handleDownload = async (item) => {
    try {
      await downloadFileS3(item.url, `${item.title}.pdf`);
    } catch (err) {
      console.error('No se pudo descargar el archivo', err);
      showSnackbar(t('s3_download_error'), 'error');
    }
  };

  const handleOpenPreview = async (item) => {
    try {
      setLoadingPreview(true);
      setPreviewDoc(item);
      const blobUrl = await getFilePreviewUrlS3(item.url);
      setPreviewUrl(blobUrl);
    } catch (err) {
      console.error('No se pudo cargar la vista previa del archivo', err);
      showSnackbar(t('s3_preview_error'), 'error');
    } finally {
      setLoadingPreview(false);
    }
  };

  const handleClosePreview = () => {
    if (previewUrl) {
      window.URL.revokeObjectURL(previewUrl);
    }
    setPreviewUrl(null);
    setPreviewDoc(null);
  };

  const handleExportExcel = async () => {
    try {
      setIsExportingExcel(true);
      await exportDailyStatesExcel(filters);
    } catch (error) {
      console.error('Error descargando Excel:', error);
    } finally {
      setIsExportingExcel(false);
    }
  };

  const hasActionsColumn = canGoToCaseDetail || canManage;

  const tableGridTemplate = hasActionsColumn
    ? `
    minmax(min-content, min-content)
    minmax(max-content, max-content)
    minmax(200px, min-content)
    minmax(0, 1fr)
    minmax(0, 120px)
    minmax(0, 100px)
    minmax(0, 1fr)
    minmax(0, 70px)
    minmax(0, 100px)
  `
    : `
    minmax(min-content, min-content)
    minmax(max-content, max-content)
    minmax(200px, min-content)
    minmax(0, 1fr)
    minmax(0, 120px)
    minmax(0, 100px)
    minmax(0, 1fr)
    minmax(0, 70px)
  `;

  const tableColumns = useMemo(() => {
    const cols = [
      {
        accessor: 'caseStateProcessId',
        sort: true,
        render: (info) => {
          const isActive =
            String(info.internalStateStatus ?? '')
              .trim()
              .toLowerCase() === 'activa';
          const label = isActive
            ? t('dailyStates_activeStatus')
            : t('dailyStates_inactiveStatus');
          const { fill } = getInternalStateConfig(info.state);
          return (
            <div className='d-flex justify-content-center align-items-center gap-1'>
              {SvgIcon && info.priority === 'Alta' && (
                <SvgIcon
                  name='exclamationTriangle'
                  fill={fill}
                  size='14px'
                  color='var(--error-500)'
                />
              )}
              <span
                className={`daily-state__status-dot ${
                  isActive ? 'is-active' : 'is-inactive'
                }`}
                aria-label={label}
                title={label}
              />
            </div>
          );
        },
      },
      {
        accessor: 'code',
        label: t('dailyStates_roleColumn'),
        sort: true,
      },
      {
        accessor: 'title',
        label: t('dailyStates_caseColumn'),
        sort: true,
      },
      {
        accessor: 'courtName',
        label: t('dailyStates_courtFilter'),
        sort: true,
      },
      {
        accessor: 'process',
        label: t('dailyStates_processColumn'),
        sort: true,
      },
      {
        accessor: 'description',
        label: t('dailyStates_descriptionColumn'),
        sort: true,
      },
      {
        accessor: 'date',
        label: t('dailyStates_dateColumn'),
        sort: true,
        render: (info) => (
          <span>{formatDateByLocale(info.date, dailyStateLanguage)}</span>
        ),
      },
      {
        accessor: 'state',
        label: t('dailyStates_internalStatusColumn'),
        sort: true,
        render: (info) => {
          const { icon, variant, fill } = getInternalStateConfig(info.state);
          return (
            <div className='d-flex justify-content-center align-items-center gap-1'>
              <span
                className={`daily-state__status-icon daily-state__status-icon--${variant}`}
                aria-label={info.state}
                title={info.state}
              >
                {SvgIcon && <SvgIcon name={icon} fill={fill} size='14px' />}
              </span>
            </div>
          );
        },
      },
      {
        accessor: 'businessArea',
        label: t('dailyStates_businessAreaColumn'),
        sort: true,
      },
      {
        accessor: 'url',
        label: t('dailyStates_documentColumn'),
        render: (info) => (
          <div className='d-flex justify-content-center'>
            {info.url && (
              <IconButton
                ariaLabel={t('dailyStates_documentAriaLabel')}
                icon='fileEarmarkText'
                onClick={() => handleOpenPreview(info)}
                size='md'
              />
            )}
          </div>
        ),
      },
    ];

    if (canGoToCaseDetail || canManage) {
      cols.push({
        accessor: 'state',
        label: t('dailyStates_actionsColumn'),
        render: (info) => (
          <div className='d-flex align-items-center'>
            {canGoToCaseDetail && (
              <Link
                to={`/detalle-causa/${info.id}/movimientos`}
                state={{
                  caseData: {
                    ...info,
                    civilCourtCode: info.civilCourtCode,
                    newCivilCourtCode: info.newCivilCourtCode,
                    process: info.process,
                    description: info.description,
                    businessAreaId: info.businessAreaId,
                  },
                }}
                onClick={() => {
                  const payload = {
                    ...info,
                    civilCourtCode: info.civilCourtCode,
                    newCivilCourtCode: info.newCivilCourtCode,
                    process: info.process,
                    description: info.description,
                    businessAreaId: info.businessAreaId,
                  };
                  localStorage.setItem(
                    'dailyCaseData',
                    JSON.stringify(payload)
                  );
                }}
                className='text-decoration-none'
              >
                <IconButton
                  ariaLabel={t('dailyStates_viewAriaLabel')}
                  icon='eye'
                  size='md'
                />
              </Link>
            )}
            {canManage && (
              <Dropdown
                ariaLabel={t('dailyStates_openStateMenuAriaLabel')}
                icon='threeDotsVertical'
                onSelect={(selected) => handleState(selected, info)}
                options={states.slice(1)}
                variant='icon'
              />
            )}
          </div>
        ),
      });
    }

    return cols;
  }, [
    SvgIcon,
    canGoToCaseDetail,
    canManage,
    dailyStateLanguage,
    handleState,
    states,
    t,
  ]);

  return (
    <div>
      {/* HEADER */}
      <div className='mb-4'>
        <h2 className=' fw-semibold'>{t('dailyStates_title')}</h2>
        <h4 className=' fw-normal'>{t('dailyStates_subtitle')}</h4>
      </div>
      {/* STATS */}
      <div className='row mb-4'>
        <div className='col-12 col-md-3'>
          <CardStats
            DesignSystemComponents={DesignSystemComponents}
            title={t('dailyStates_urgents')}
            count={criticalPendingCount}
            subtitle={t('dailyStates_unmanaged')}
            icon={'exclamationTriangle'}
            loading={loading}
            fill
            type='error'
          />
        </div>
        <div className='col-12 col-md-3'>
          <CardStats
            DesignSystemComponents={DesignSystemComponents}
            title={t('dailyStates_pendings')}
            count={pendingCount}
            subtitle={t('dailyStates_unmanaged')}
            type='warning'
            icon={'clock'}
            loading={loading}
          />
        </div>
        <div className='col-12 col-md-3'>
          <CardStats
            DesignSystemComponents={DesignSystemComponents}
            title={t('dailyStates_resolved')}
            count={managedCount}
            subtitle={t('dailyStates_managed')}
            icon={'check2Circle'}
            type='success'
            loading={loading}
            fill
          />
        </div>
        <div className='col-12 col-md-3'>
          <CardStats
            DesignSystemComponents={DesignSystemComponents}
            title={t('dailyStates_total')}
            count={totalCount}
            subtitle={t('dailyStates_toDate')}
            icon={'briefcase'}
            loading={loading}
          />
        </div>
      </div>
      {/* INFO MESSAGE */}
      {!showInactiveWarning ? (
        <div className='mb-3 p-3 rounded bg-light border'>
          <p className='mb-1 fw-semibold'>
            {/* Por defecto, solo se muestran causas activas. */}
            {t('dailyStates_info_preciseSearch_title')}
          </p>
          <p className='mb-0 caption font-light'>
            {/* Para buscar causas inactivas, utiliza los filtros avanzados. */}
            {t('dailyStates_info_preciseSearch_subtitle')}
          </p>
        </div>
      ) : (
        <div className='mb-3'>
          <div className='p-3 rounded border border-warning bg-warning-subtle d-flex align-items-start gap-2'>
            <IconButton
              icon='hourglassSplit'
              size='sm'
              variant='naked'
              ariaLabel={t('dailyStates_inactiveWarning_aria')}
            />
            <div>
              <p className='mb-1 fw-semibold text-warning'>
                {t('dailyStates_inactiveWarning_title')}
              </p>
              <p className='mb-0 caption font-light'>
                {t('dailyStates_inactiveWarning_subtitle')}
              </p>
            </div>
          </div>
        </div>
      )}

      {/* SEARCHER */}
      <Card specialPadding='p-4'>
        {canDownloadExcel && (
          <div className='row'>
            <div className='d-flex justify-content-end pb-3'>
              <Button
                iconStart='download'
                label={t('dailyStates_downloadExcel')}
                variant='naked'
                className='mt-2'
                size='sm'
                onClick={handleExportExcel}
                disabled={isExportingExcel}
              />
            </div>
          </div>
        )}
        <div className='row'>
          <div className='col-3'>
            <Input
              icon='search'
              name='searchDailyState'
              onChange={handleSearchDailyState}
              placeholder={t('dailyStates_searchPlaceholder')}
              type='text'
              value={searchDailyState}
            />
          </div>
          <div className='col-2 '>
            <Input
              type='date'
              value={filters?.StartDate ?? ''}
              onChange={(e) => {
                const value = e.target.value || null;

                handleFilterChange('StartDate', value);
                handleFilterChange('EndDate', null); // se fuerza null
              }}
            />
          </div>
          <div className='col-3'>
            <ComboBox
              placeholder={t('dailyStates_groupFilter')}
              options={businessAreaOptions}
              value={selectedBusinessAreaMulti}
              onChange={handleGroupsMultiSelect}
              multiSelect
            />
          </div>
          <div className='col-3'>
            <ComboBox
              options={courtsOptions}
              onChange={handleCourtsFilter}
              value={selectedCourts}
              loading={loadingMyCourts}
            />
          </div>
          <IconButton
            ariaLabel={t('dailyStates_searchAriaLabel')}
            icon='search'
            onClick={handleApplyFilters}
            className='mt-1'
            size='lg'
          />
          <IconButton
            ariaLabel={t('dailyStates_filterAriaLabel')}
            icon='funnel'
            onClick={() => setShowPanel((prev) => !prev)}
            className='mt-1'
            size='lg'
          />
        </div>
        {!showPanel && (
          <FilterChips
            filters={buildFilterChips()}
            onRemove={handleRemoveFilterChip}
            onClearAll={handleClearAllFilterChips}
          />
        )}
        {showPanel && (
          <ExpandablePanel
            open={showPanel}
            showActions
            onApply={handleApplyFilters}
            onClear={handleClearFilters}
            onClose={() => setShowPanel(false)}
            applyLabel={t('dailyStates_applyFiltersButton')}
            clearLabel={t('dailyStates_clearFiltersButton')}
            cancelLabel={t('dailyStates_closeFilterButton')}
          >
            <div className='d-flex align-items-center justify-content-between mb-3'>
              <div>
                <h4 className='fw-semibold '>{t('dailyStates_filterTitle')}</h4>
                <p className='mini font-light fw-normal'>
                  {t('dailyStates_filterSubtitle')}
                </p>
              </div>
              <IconButton
                ariaLabel={t('dailyStates_closePanelAriaLabel')}
                icon='close'
                size='md'
                onClick={() => setShowPanel(false)}
              />
            </div>
            <div className='row'>
              <div className='col-3'>
                <Input
                  label={t('dailyStates_roleFilter')}
                  name='role'
                  placeholder={t('dailyStates_rolePlaceholder')}
                  type='text'
                  onChange={(e) => {
                    handleFilterChange('Role', e.target.value);
                  }}
                  value={filters.Role}
                />
              </div>
              <div className='col-3'>
                <Input
                  label={t('dailyStates_caseFilter')}
                  name='case'
                  placeholder={t('dailyStates_casePlaceholder')}
                  type='text'
                  onChange={(e) => {
                    handleFilterChange('Case', e.target.value);
                  }}
                  value={filters.Case}
                />
              </div>
              <div className='col-3'>
                <Dropdown
                  label={t('dailyStates_myCasesFilter')}
                  options={myCasesOptions}
                  value={myCases}
                  onChange={handleMyCases}
                />
              </div>
              <div className='col-3'>
                <ComboBox
                  label={t('dailyStates_processFilter')}
                  placeholder={t('dailyStates_processPlaceholder')}
                  options={processOptions}
                  value={selectedProcess}
                  multiSelect
                  onChange={(selectedItems) => {
                    setSelectedProcess(selectedItems);
                    handleFilterChange(
                      'Process',
                      selectedItems
                        ? selectedItems.map((item) => item.value)
                        : []
                    );
                  }}
                />
              </div>
            </div>
            <div className='row py-3'>
              <div className='col-3'>
                <div style={{ marginBottom: '20px' }}>
                  <Dropdown
                    label={t('dailyStates_withDocumentFilter')}
                    options={withDocuments}
                    value={selectedWithDocuments}
                    onChange={handleHasDocument}
                  />
                </div>
              </div>
              <div className='col-3'>
                <div style={{ marginBottom: '20px' }}>
                  <Dropdown
                    label={t('dailyStates_activeFilter')}
                    options={activeCases}
                    value={selectedActiveCases}
                    onChange={handleActiveCases}
                  />
                </div>
              </div>
              <div className='col-3'>
                <div style={{ marginBottom: '20px' }}>
                  <Dropdown
                    label={t('dailyStates_managementFilterLabel')}
                    placeholder={t('dailyStates_managementFilterPlaceholder')}
                    options={states}
                    value={selectedInternalStatus}
                    onChange={(selected) => {
                      handleFilterChange(
                        'States',
                        selected.value === null ? [] : [selected.value]
                      );
                    }}
                  />
                </div>
              </div>
            </div>
          </ExpandablePanel>
        )}
        <div
          className={`table-container ${canGoToCaseDetail || canManage ? 'table-daily-state' : 'table-daily-state-no-actions'}`}
          style={{
            '--data-table-library_grid-template-columns': tableGridTemplate,
          }}
        >
          <Table
            columns={tableColumns}
            data={dailyState}
            page={dailyStateTablePage}
            onPageChange={(page) => {
              setDailyStateTablePage(page);

              persistStateToUrl({
                filters,
                page,
                pageSize,
                setSearchParams,
              });
            }}
            pageSize={pageSize}
            onPageSizeChange={handlePageSizeChange}
            paginator
            paginatorGroup={[
              { label: '10', value: 10 },
              { label: '20', value: 20 },
              { label: '50', value: 50 },
              { label: '100', value: 100 },
            ]}
            loading={loading}
            sortFns={sortFns}
            // paginationMode='server'
            // totalItems={totalCount}
          />
          {!loading && hasSearched && dailyState.length === 0 && (
            <div className='text-center p-4'>
              <p className='caption font-light'>{t('dailyStates_noResults')}</p>
            </div>
          )}
        </div>
      </Card>
      {Modal && previewDoc && (
        <Modal
          title={t('dailyStates_previewTitle')}
          show={!!previewDoc}
          onClose={handleClosePreview}
          primaryBtnTxt={t('dailyStates_downloadButton')}
          primaryBtnAction={() => {
            if (previewDoc) {
              handleDownload(previewDoc);
            }
          }}
          secondaryBtnTxt={t('dailyStates_closeButton')}
          secondaryBtnAction={handleClosePreview}
          ctaAligment='right'
          width='900px'
        >
          <div style={{ height: '70vh' }}>
            {loadingPreview && (
              <p className='caption font-light'>
                {t('dailyStates_loadingPreview')}
              </p>
            )}
            {!loadingPreview && previewUrl && (
              <PdfViewer pdf={previewUrl} className='h-100' />
            )}
            {!loadingPreview && !previewUrl && (
              <p className='caption font-light'>
                {t('dailyStates_previewLoadError')}
              </p>
            )}
          </div>
        </Modal>
      )}
    </div>
  );
};
export default DailyState;
